<template>
  <transition name="modal">
    <div class="modal-overlay">
      <div class="modal-wrapper showSweetAlert visible" data-custom-class="" data-has-cancel-button="true" data-has-confirm-button="true" data-allow-outside-click="false" data-has-done-function="true" data-animation="pop" data-timer="null">

        <h2 class="modal-title">{{ isEditing ? 'Edit Additional Investment' : 'Add Funds to My Reinvestments' }}</h2>
        <p class="modal-paragraph">
          {{ isEditing ? 'Update your additional investment details below.' : 'Select a note and enter the amount you want to add to your reinvestments.' }}
        </p>

        <div class="modal-info-box" v-if="!isEditing">
          <p class="info-text">
            <em class="fa fa-info-circle"></em>
            You can add additional funds to your reinvestments between <strong>45 and 3 days</strong> before the reinvestment date.
            Only notes within this window are shown below.
          </p>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label>Select note</label>
            <select
              v-model="selectedNote"
              name="note"
              class="form-control input-lg"
              required
            >
              <option :value="null" disabled selected hidden>Select note</option>
              <option
                v-for="(note, index) in availableNotes"
                :key="index"
                :value="note"
              >
                Note {{ note.noteId }} - Reinvests {{ formatDate(note.maturityDate) }}
              </option>
            </select>
            <span
              v-if="!selectedNote"
              class="error-feedback"
            >
              Please select a note
            </span>
          </div>

          <div v-if="selectedNote">
            <div v-if="fundingSources && fundingSources.length > 0" class="form-group">
              <label>Select bank account</label>
              <select
                v-model="selectedFundingSource"
                name="fundingSource"
                class="form-control input-lg"
                required
              >
                <option :value="null" disabled selected hidden>Select bank account</option>
                <option
                  v-for="(fs, index) in fundingSources"
                  :key="index"
                  :value="fs"
                  :disabled="fs.removed"
                >
                  {{ fs.name }}
                </option>
              </select>
              <span
                v-if="!selectedFundingSource"
                class="error-feedback"
              >
                Please select a bank account
              </span>
            </div>
            <div v-else class="alert alert-warning no-bank-account-alert">
              <p class="no-bank-text">
                You must connect a bank account to make ACH transfers.
                <router-link :to="{ name: 'AddFundingSource' }" class="bank-link">
                  Add a bank account
                </router-link>
              </p>
            </div>

            <div class="form-group">
              <label>Amount</label>
              <input
                v-model="amount"
                v-money="money"
                type="text"
                class="form-control input-lg"
                placeholder="$0.00"
                required
              />
              <span
                v-if="amountError"
                class="error-feedback"
              >
                {{ amountError }}
              </span>
            </div>
          </div>
        </div>

        <div class="modal-buttons">
          <div class="row">
            <div class="col-md-12 text-center">
              <button
                type="button"
                @click="closeModal()"
                class="btn btn-info btn-lg btn-cancel"
              >
                Cancel
              </button>
              <button
                v-if="selectedNote"
                type="button"
                @click="submitAddMoney()"
                class="btn btn-info btn-lg btn-accept"
                :disabled="!isFormValid || loading"
              >
                {{ loading ? 'Submitting...' : (isEditing ? 'Update Investment' : 'Add Money') }}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
import { formatDate, formatAmount } from '@/util';
import { VMoney } from 'v-money';

export default {
  name: 'AddMoney',
  directives: {
    money: VMoney,
  },
  props: {
    fundingSources: {
      type: Array,
      required: true,
      default: () => [],
    },
    availableNotes: {
      type: Array,
      required: true,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    editingInvestment: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedFundingSource: null,
      amount: '$0.00',
      selectedNote: null,
      amountError: '',
      money: {
        decimal: '.',
        thousands: ',',
        prefix: '$',
        precision: 2,
      },
    };
  },
  computed: {
    isEditing() {
      return this.editingInvestment !== null;
    },
    isFormValid() {
      return (
        this.selectedNote &&
        this.selectedFundingSource &&
        !this.selectedFundingSource.removed &&
        this.amount &&
        this.isAmountValid()
      );
    },
  },
  mounted() {
    if (this.editingInvestment) {
      const note = this.availableNotes.find(
        n => n.noteId === this.editingInvestment.noteId,
      );
      if (note) {
        this.selectedNote = note;
      }
      this.amount = `$${parseFloat(this.editingInvestment.amount).toFixed(2)}`;
      const fundingSource = this.fundingSources.find(
        fs => fs.type === this.editingInvestment.fundingSourceType,
      );
      if (fundingSource) {
        this.selectedFundingSource = fundingSource;
      }
    }
  },
  methods: {
    formatDate,
    formatAmount,
    isAmountValid() {
      const amountValue = parseFloat(this.amount.replace(/[^0-9.]/g, ''));
      const minimumAmount = 20;
      if (!amountValue || isNaN(amountValue)) {
        this.amountError = 'Please enter a valid amount';
        return false;
      }
      if (amountValue < minimumAmount) {
        this.amountError = `Amount must be at least $${minimumAmount}`;
        return false;
      }
      this.amountError = '';
      return true;
    },
    closeModal() {
      this.$emit('close');
    },
    submitAddMoney() {
      if (!this.isFormValid) return;

      const amountValue = parseFloat(this.amount.replace(/[^0-9.]/g, ''));
      const payload = {
        noteId: this.selectedNote.noteId,
        fundingSourceId: this.selectedFundingSource.id,
        fundingSourceType: this.selectedFundingSource.type,
        fundingSourceName: this.selectedFundingSource.name,
        amount: amountValue,
      };

      this.$emit('submit', payload);
    },
  },
  watch: {
    amount() {
      if (this.amount && this.amount !== '$0.00') {
        this.isAmountValid();
      } else {
        this.amountError = '';
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow-y: auto;
  padding: 15px;
  -webkit-overflow-scrolling: touch;
  padding-top: 100px;
}

.modal-wrapper {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  display: block;
  margin: auto;
  flex-shrink: 0;
  width: 90%;
  max-width: 580px;
  max-height: calc(100vh - 30px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.modal-wrapper p {
  font-size: 16px;
  text-align: center;
}
.modal-title {
  color: #127285;
  text-align: center;
}
.modal-paragraph {
  margin-bottom: 15px;
}
.modal-info-box {
  background-color: #e8f4f8;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  padding: 12px 15px;
  margin: 0 20px 15px 20px;
}
.info-text {
  font-size: 14px;
  line-height: 1.5;
  color: #0c5460;
  margin-bottom: 0;
}
.info-text .fa {
  margin-right: 8px;
}
.modal-body {
  padding: 20px;
  text-align: left;
}
.modal-body .form-group {
  margin-bottom: 20px;
}
.modal-body label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}
.modal-buttons {
  position: relative;
  padding: 15px;
  margin-top: 20px;
}
.modal-buttons .btn {
  min-width: 140px;
  margin: 0 5px;
  white-space: nowrap;
}
.error-feedback {
  color: #f05050;
  font-size: 12px;
  display: block;
  margin-top: 5px;
}
.no-bank-account-alert {
  margin-top: 15px;
  margin-bottom: 15px;
}
.no-bank-text {
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 0;
}
.bank-link {
  color: #127285;
  font-weight: bold;
  text-decoration: underline;
}
.bank-link:hover {
  color: #0d5563;
}
</style>



// WEBPACK FOOTER //
// AddMoney.vue?f9bd0ed0